import { ArrowDownTrayIcon, CalendarIcon, ChartBarIcon, DocumentChartBarIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { adminService } from "../../services/api";

const Reports = () => {
    const [salesData, setSalesData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        period: "30d",
        groupBy: "day",
    });

    const fetchReports = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const salesResponse = await adminService.getSalesReport(filters);

            if (salesResponse?.data) {
                setSalesData(salesResponse.data);
            }
        } catch (error) {
            console.error("Error fetching reports:", error);
            setError("Failed to load reports. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const exportData = () => {
        if (!salesData) return;
        const jsonString = JSON.stringify(salesData, null, 2);
        const blob = new Blob([jsonString], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `sales-report-${new Date().toISOString().split("T")[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center justify-center h-64"
            >
                <div className="relative">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-gradient-to-r from-green-200 to-emerald-200"></div>
                    <div className="absolute top-0 left-0 animate-spin rounded-full h-16 w-16 border-4 border-transparent border-t-green-500"></div>
                </div>
            </motion.div>
        );
    }

    if (error) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center justify-center h-64 space-y-4"
            >
                <p className="text-red-600 font-bold text-lg">{error}</p>
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={fetchReports}
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-bold"
                >
                    Retry
                </motion.button>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6 sm:space-y-8"
        >
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0"
            >
                <div>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-[#008000] to-emerald-600 bg-clip-text text-transparent">
                        Reports & Analytics
                    </h2>
                    <p className="text-gray-600 text-sm sm:text-base lg:text-lg font-medium mt-2">
                        Detailed insights and performance metrics
                    </p>
                </div>
                <motion.div
                    className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <motion.select
                        whileFocus={{ scale: 1.02 }}
                        value={filters.period}
                        onChange={e => setFilters(prev => ({ ...prev, period: e.target.value }))}
                        className="border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 font-medium w-full sm:w-auto"
                    >
                        <option value="7d">Last 7 days</option>
                        <option value="30d">Last 30 days</option>
                        <option value="90d">Last 90 days</option>
                        <option value="1y">Last year</option>
                    </motion.select>
                    <motion.select
                        whileFocus={{ scale: 1.02 }}
                        value={filters.groupBy}
                        onChange={e => setFilters(prev => ({ ...prev, groupBy: e.target.value }))}
                        className="border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 font-medium w-full sm:w-auto"
                    >
                        <option value="day">Daily</option>
                        <option value="month">Monthly</option>
                    </motion.select>
                </motion.div>
            </motion.div>

            {/* Sales Report */}
            <AnimatePresence>
                {salesData && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: 0.1 }}
                        className="space-y-8"
                    >
                        <motion.div
                            className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                Sales Performance
                            </h3>
                            <motion.button
                                whileHover={{ scale: 1.05, y: -2 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={exportData}
                                className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:shadow-lg transition-all duration-200 font-bold text-sm w-full sm:w-auto"
                            >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                                <span>Export Data</span>
                            </motion.button>
                        </motion.div>

                        {/* Sales Summary Cards */}
                        <motion.div
                            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, staggerChildren: 0.1 }}
                        >
                            <motion.div
                                whileHover={{ scale: 1.02, y: -4 }}
                                className="bg-gradient-to-br from-white to-gray-50 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg p-4 sm:p-6"
                            >
                                <div className="flex items-center">
                                    <motion.div
                                        whileHover={{ scale: 1.1 }}
                                        className="p-2.5 sm:p-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500"
                                    >
                                        <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                                    </motion.div>
                                    <div className="ml-3 sm:ml-4">
                                        <p className="text-xs sm:text-sm font-bold text-gray-600">Total Revenue</p>
                                        <p className="text-lg sm:text-2xl font-bold text-gray-900">
                                            ₹
                                            {salesData.salesData
                                                .reduce((sum, item) => sum + item.revenue, 0)
                                                .toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div
                                whileHover={{ scale: 1.02, y: -4 }}
                                className="bg-gradient-to-br from-white to-gray-50 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg p-4 sm:p-6"
                            >
                                <div className="flex items-center">
                                    <motion.div
                                        whileHover={{ scale: 1.1 }}
                                        className="p-2.5 sm:p-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500"
                                    >
                                        <DocumentChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                                    </motion.div>
                                    <div className="ml-3 sm:ml-4">
                                        <p className="text-xs sm:text-sm font-bold text-gray-600">Total Orders</p>
                                        <p className="text-lg sm:text-2xl font-bold text-gray-900">
                                            {salesData.salesData.reduce((sum, item) => sum + item.orders, 0)}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div
                                whileHover={{ scale: 1.02, y: -4 }}
                                className="bg-gradient-to-br from-white to-gray-50 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg p-4 sm:p-6 sm:col-span-2 lg:col-span-1"
                            >
                                <div className="flex items-center">
                                    <motion.div
                                        whileHover={{ scale: 1.1 }}
                                        className="p-2.5 sm:p-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500"
                                    >
                                        <CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                                    </motion.div>
                                    <div className="ml-3 sm:ml-4">
                                        <p className="text-xs sm:text-sm font-bold text-gray-600">Avg Order Value</p>
                                        <p className="text-lg sm:text-2xl font-bold text-gray-900">
                                            ₹
                                            {salesData.salesData.length > 0
                                                ? (
                                                    salesData.salesData.reduce((sum, item) => sum + item.revenue, 0) /
                                                    salesData.salesData.reduce((sum, item) => sum + item.orders, 0)
                                                ).toFixed(0)
                                                : 0}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>

                        {/* Sales Chart Data Table */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="bg-gradient-to-br from-white to-gray-50 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg overflow-hidden"
                        >
                            <div className="p-4 sm:p-6">
                                <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">
                                    Sales Breakdown
                                </h4>

                                {/* Desktop Table */}
                                <div className="hidden lg:block overflow-x-auto">
                                    <table className="min-w-full">
                                        <thead className="bg-gradient-to-r from-green-500 to-emerald-500">
                                            <tr>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">
                                                    Date
                                                </th>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">
                                                    Orders
                                                </th>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">
                                                    Revenue
                                                </th>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">
                                                    Avg Order Value
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {salesData.salesData.map((item, index) => (
                                                <motion.tr
                                                    key={index}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: 0.1 * index }}
                                                    whileHover={{ backgroundColor: "rgba(16, 185, 129, 0.05)" }}
                                                    className="transition-all duration-200"
                                                >
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                                        {(() => {
                                                            const dateStr = item._id;
                                                            if (dateStr.includes('-')) {
                                                                if (dateStr.length === 10) {
                                                                    return new Date(dateStr + 'T00:00:00').toLocaleDateString();
                                                                } else if (dateStr.length === 7) {
                                                                    const [year, month] = dateStr.split('-');
                                                                    return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                                                                }
                                                            }
                                                            return dateStr;
                                                        })()}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                                        {item.orders}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-[#008000]">
                                                        ₹{item.revenue.toLocaleString()}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">
                                                        ₹{item.avgOrderValue.toFixed(0)}
                                                    </td>
                                                </motion.tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Card Layout */}
                                <div className="lg:hidden space-y-3 max-h-96 overflow-y-auto">
                                    {salesData.salesData.map((item, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.1 * index }}
                                            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow duration-200"
                                        >
                                            <div className="flex justify-between items-start mb-3">
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900">
                                                        {(() => {
                                                            const dateStr = item._id;
                                                            if (dateStr.includes('-')) {
                                                                if (dateStr.length === 10) {
                                                                    return new Date(dateStr + 'T00:00:00').toLocaleDateString();
                                                                } else if (dateStr.length === 7) {
                                                                    const [year, month] = dateStr.split('-');
                                                                    return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                                                                }
                                                            }
                                                            return dateStr;
                                                        })()}
                                                    </p>
                                                    <p className="text-xs text-gray-500">Date</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-bold text-gray-900">{item.orders}</p>
                                                    <p className="text-xs text-gray-500">Orders</p>
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <p className="text-sm font-bold text-[#008000]">
                                                        ₹{item.revenue.toLocaleString()}
                                                    </p>
                                                    <p className="text-xs text-gray-500">Revenue</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-bold text-blue-600">
                                                        ₹{item.avgOrderValue.toFixed(0)}
                                                    </p>
                                                    <p className="text-xs text-gray-500">Avg Order</p>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default Reports;
